<script setup>
import { PlayerStore } from '@/front/stores/player_store.js'
import { ClickStore } from '@/front/stores/click_store.js'
import { AnnStore } from '@/front/stores/ann_store.js'
import { DesignStore } from '@/front/stores/design_store.js'
import { VideoStore } from '@/front/stores/video_store.js'
import { defineAsyncComponent } from 'vue'
import { Cancel01Icon } from 'hugeicons-vue'
import ProgressBar from 'primevue/progressbar'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { formatSeconds } from '@/front/composables/TimeFormatter.js'

import { finishLink } from '@/front/composables/ClickComposer.js'
// Store initialization
const playerStore = PlayerStore()
const clickStore = ClickStore()
const designStore = DesignStore()
const annStore = AnnStore()
const videoStore = VideoStore()
import { useGlobalRenderTracker } from '@/front/composables/GlobalRenderTracker.js'
if (playerStore.isDebugPerformance) {
  useGlobalRenderTracker('DialogLayer', { logLevel: 'none' })
}

import MessageLayer from '@/front/components/MessageLayer.vue'
import QuestionLayer from '@/front/components/QuestionLayer.vue'

// const MagicMenuLayer = defineAsyncComponent(() => 
//   import('@/front/components/MagicMenuLayer.vue')
// )
const MagicMenuLayer = defineAsyncComponent(() => 
  import('@/front/components/MagicMenuLayer.vue')
)
const ImageLayer = defineAsyncComponent(() => 
  import('@/front/components/ImageLayer.vue')
)
const ArticleLayer = defineAsyncComponent(() => 
  import('@/front/components/ArticleLayer.vue')
)
const GenieLayer = defineAsyncComponent(() => 
  import('@/front/components/GenieLayer.vue')
)
const ChatGPTLayer = defineAsyncComponent(() => 
  import('@/front/components/ChatGPTLayer.vue')
)
const CollectLayer = defineAsyncComponent(() => 
  import('@/front/components/CollectLayer.vue')
)
// const ClipLayer = defineAsyncComponent(() => 
  // import('@/front/components/ClipLayer.vue')
// )
// const AudioLayer = defineAsyncComponent(() => 
//   import('@/front/components/AudioLayer.vue')
// )
import AudioLayer from '@/front/components/AudioLayer.vue'
import ClipLayer from '@/front/components/ClipLayer.vue'

const DrawViewLayer = defineAsyncComponent(() => 
  import('@/front/components/DrawViewLayer.vue')
)
const CaptureLeadLayer = defineAsyncComponent(() => 
  import('@/front/components/CaptureLeadLayer.vue')
)
const ParticipateLayer = defineAsyncComponent(() => 
  import('@/front/components/ParticipateLayer.vue')
)

</script>

<template>
  <Transition :name="clickStore.questionActive ? clickStore.questionActive.transition || 'fade' : 'none'" mode="out-in" appear style="--animate-duration: 0.5s;">
    <QuestionLayer v-if="clickStore.questionActive && !clickStore.clipActive && !clickStore.magicMenuActive" :readonly="annStore.isEditing || annStore.isBulkEditing"  :style="{ opacity: clickStore.activeMessage ? 0 : 1 }"/>
  </Transition>

  <Transition :name="videoStore.video.design.message_transition || 'fade'" mode="in" appear :style="{ '--animate-duration': clickStore.questionActive ? '10ms' : '0.5s' }">
    <MessageLayer v-if="clickStore.activeMessage" />
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
      <ImageLayer v-if="clickStore.activeImageUrl"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <ArticleLayer v-if="clickStore.activeArticle"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <GenieLayer v-if="clickStore.genieActive"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <ChatGPTLayer v-if="clickStore.chatGPTActive"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <CollectLayer v-if="clickStore.activeCollect"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <Dialog v-model:visible="clickStore.participateActive" modal :header="$t('common.add_comment')" class="w-[90%] max-w-xl">
      <ParticipateLayer/>
    </Dialog>
  </Transition>

  <!-- Used for playback - editing is in -->
  <!-- <Suspense v-if="clickStore.magicMenuActive && !clickStore.activeMessage">
    <MagicMenuLayer />
  </Suspense> -->


  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <ClipLayer v-if="!annStore.isEditing && (clickStore.clipActive || clickStore.interactionClipActive)" :ann="clickStore.activeClipAnn"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <AudioLayer v-if="clickStore.audioActive"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <CaptureLeadLayer v-if="clickStore.captureLeadActive"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <DrawViewLayer v-if="clickStore.activeDrawing" class="w-full h-full"/>
  </Transition>

  <Transition name="fade" mode="out-in" appear style="--animate-duration: 0.3s;">
    <div v-if="clickStore.activeTimedPause && videoStore.video?.design.show_countdown_bar" id="pause-progress" class="w-full absolute top-0 left-0 w-full bg-white/30 h-4 @md/player:h-6 @lg/player:h-8">
      <div class="absolute top-0 left-0 w-full h-full" :style="{ width: clickStore.activePauseAmounts.progress + '%', backgroundColor: videoStore.video.design.color }"></div>
      <div class="absolute top-0 left-0 w-full h-full flex items-center justify-center text-white text-lg font-bold">
        {{ formatSeconds(clickStore.activePauseAmounts.remaining/1000, false) }}
      </div>
    </div>
  </Transition>

  <!-- Modal embed -->
  <Dialog v-model:visible="clickStore.activeLink" modal class="w-[90vw] h-[90vh] select-none bg-black">
    <iframe :src="clickStore.activeLink" class="w-full h-full fixed left-0 top-0 rounded-lg" />
    <div class="fixed -top-3 -right-3 z-50">
      <button @click.stop="finishLink" class="!bg-[var(--user-theme-color)] rounded-full p-1 h-10 w-10 border-[1px] border-white">
        <Cancel01Icon class="w-[80%] h-[80%] aspect-square mx-auto text-white" />
      </button>
    </div>
  </Dialog>
</template>

<style scoped>
.loading-state {
  @apply w-full h-full flex items-center justify-center;
  min-height: 50px;
}
</style>

<style>
/* Override default primevue dialog header */
#link-dialog [data-pc-section="header"] {
  @apply !hidden;
}
#link-dialog [data-pc-section="content"] {
  @apply !bg-black;
}
</style>